import { Link } from "react-router-dom";
import { ContactDialog } from "@/components/marketing/ContactDialog";
import { FEATURE_GROUPS, featurePagePath, groupedFeaturePages } from "@/lib/featurePages";

/**
 * Full-width bordered footer, laid out like the Secretarius site footer:
 * a multi-column link grid over a thin legal bar.
 */
export const MarketingFooter = () => {
  const groups = groupedFeaturePages();
  const highlight = FEATURE_GROUPS.map((g) => groups[g.key]?.[0]).filter(Boolean).slice(0, 5);

  return (
    <footer className="mt-16 border-t border-border bg-card/40">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-5">
        <div className="sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2">
            <span
              role="img"
              aria-label="Excerptus logo"
              className="inline-block h-6 w-6 bg-primary"
              style={{
                WebkitMaskImage: "url(/excerptus-logo.webp)",
                maskImage: "url(/excerptus-logo.webp)",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
            <span className="font-serif text-lg font-semibold text-primary">Excerptus</span>
          </div>
          <p className="mt-3 max-w-xs text-xs leading-relaxed text-muted-foreground">
            The heart of the Knowledge Worker's second brain — a citable library and canonical
            excerpts that ground the content you publish.
          </p>
        </div>

        <div>
          <div className="mkt-eyebrow mb-3">Product</div>
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            <li><Link to="/" className="hover:text-foreground">Overview</Link></li>
            <li><Link to="/pricing/" className="hover:text-foreground">Pricing</Link></li>
            <li><Link to="/help/" className="hover:text-foreground">Knowledge Base</Link></li>
            <li><Link to="/features/excerptus-affiliates/" className="hover:text-foreground">Affiliate</Link></li>
          </ul>
        </div>

        <div>
          <div className="mkt-eyebrow mb-3">Features</div>
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            {highlight.map((p) => (
              <li key={p.slug}>
                <Link to={featurePagePath(p.slug)} className="hover:text-foreground">
                  {p.navLabel}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="mkt-eyebrow mb-3">Compliance</div>
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            <li><Link to="/terms/" className="hover:text-foreground">Terms of Service</Link></li>
            <li><Link to="/privacy/" className="hover:text-foreground">Privacy Policy</Link></li>
            <li><Link to="/cookies/" className="hover:text-foreground">Cookie Notice</Link></li>
            <li><Link to="/legal/citation-styles/" className="hover:text-foreground">Citation styles</Link></li>
          </ul>
        </div>

        <div>
          <div className="mkt-eyebrow mb-3">Company</div>
          <ul className="space-y-1.5 text-[13px] text-muted-foreground">
            <li><ContactDialog /></li>
            <li>
              <a
                href="https://stats.uptimerobot.com/t1k8OOIxf4"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground"
              >
                Status
              </a>
            </li>
            <li><Link to="/auth/" className="hover:text-foreground">Sign in</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 text-[11px] text-muted-foreground sm:px-6">
          <span>©{new Date().getFullYear()} Excerptus · Knowledge Worker LLC · All Rights Reserved.</span>
          <nav className="flex flex-wrap items-center gap-4">
            <Link to="/pricing/" className="hover:text-foreground">Pricing</Link>
            <Link to="/terms/" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy/" className="hover:text-foreground">Privacy</Link>
            <Link to="/cookies/" className="hover:text-foreground">Cookies</Link>
            <Link to="/help/" className="hover:text-foreground">Knowledge Base</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
};
